/**
 * "Skoða og staðfesta": the owner of layers drawn on an older sheet revision checks that the marks
 * still fit the new version, then confirms ("Staðfesta" → engine.confirmLayerRevision through the
 * outbox, so it works offline). "Skoða fyrst" shows the layers and closes the sheets so the owner
 * can look at the music first.
 */
import { BadgeCheck, Check, Eye, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import type { LayerView } from '../annotations/contract';
import { Button, cx, Sheet } from '../components/ui';
import { useT } from '../i18n';

export function StaleReviewSheet({
  layers,
  describe,
  onConfirm,
  onLookFirst,
  onClose,
}: {
  /** Own stale layers to review; null = closed. */
  layers: LayerView[] | null;
  /** One-line description of a layer (e.g. who it is shared with). */
  describe: (l: LayerView) => string;
  onConfirm: (ids: string[]) => void;
  onLookFirst: (ids: string[]) => void;
  onClose: () => void;
}) {
  const t = useT();
  const open = layers !== null;
  const list = layers ?? [];
  // Every layer is ticked when the sheet opens or its list changes (selection is kept per list).
  const key = list.map((l) => l.id).join(',');
  const [sel, setSel] = useState<{ key: string; picked: ReadonlySet<string> }>({ key: '', picked: new Set() });
  if (!open && sel.key) setSel({ key: '', picked: new Set() }); // closed: next opening starts fully ticked
  const picked = sel.key === key ? sel.picked : new Set(list.map((l) => l.id));

  const chosen = list.filter((l) => picked.has(l.id)).map((l) => l.id);
  const multi = list.length > 1;
  const toggle = (id: string) => {
    const n = new Set(picked);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    setSel({ key, picked: n });
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('annotate.stale_review_title')}
      footer={
        list.length > 0 ? (
          <>
            <Button variant="secondary" block icon={Eye} disabled={chosen.length === 0} onClick={() => onLookFirst(chosen)}>
              {t('annotate.stale_review_look')}
            </Button>
            <Button block icon={BadgeCheck} disabled={chosen.length === 0} onClick={() => onConfirm(chosen)}>
              {multi ? t('annotate.stale_review_confirm_n', { count: chosen.length }) : t('annotate.stale_review_confirm')}
            </Button>
          </>
        ) : (
          <Button variant="secondary" block onClick={onClose}>
            {t('common.close')}
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-4 py-1">
        <div className="flex items-start gap-3 rounded-2xl bg-amber-100 px-4 py-3 text-amber-950 dark:bg-amber-900/40 dark:text-amber-100">
          <TriangleAlert className="mt-0.5 size-5 shrink-0" aria-hidden />
          <p>{t('annotate.stale_review_body1')}</p>
        </div>
        <p className="text-[var(--ink-2)]">{t('annotate.stale_review_body2')}</p>

        {list.length === 0 ? (
          <p className="text-[var(--muted)]">{t('annotate.stale_review_none')}</p>
        ) : (
          <section aria-labelledby="stale-review-layers" className="flex flex-col gap-2">
            <h3 id="stale-review-layers" className="text-[15px] font-bold tracking-wide text-[var(--muted)] uppercase">
              {t('annotate.stale_review_layers')}
            </h3>
            <ul className="flex flex-col gap-1.5">
              {list.map((l) => {
                const on = picked.has(l.id);
                const body = (
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{l.name}</span>
                    <span className="block truncate text-[14px] text-[var(--muted)]">
                      {describe(l)}
                      {l.visible ? '' : ` · ${t('annotate.stale_review_hidden')}`}
                    </span>
                  </span>
                );
                return (
                  <li key={l.id}>
                    {multi ? (
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        onClick={() => toggle(l.id)}
                        className={cx(
                          'flex min-h-14 w-full items-center gap-3 rounded-xl border px-3 text-left transition',
                          on ? 'border-[var(--accent)] bg-[var(--accent-soft)]/50' : 'border-[var(--line)] hover:bg-[var(--surface-2)]',
                        )}
                      >
                        <span
                          aria-hidden
                          className={cx(
                            'flex size-6 shrink-0 items-center justify-center rounded-md border-2',
                            on ? 'border-[var(--accent)] bg-[var(--accent)] text-[var(--accent-ink)]' : 'border-[var(--muted)]',
                          )}
                        >
                          {on ? <Check className="size-4" strokeWidth={3} /> : null}
                        </span>
                        {body}
                      </button>
                    ) : (
                      <div className="flex min-h-14 items-center gap-3 rounded-xl border border-[var(--line)] px-3">{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    </Sheet>
  );
}
